import clsx from 'clsx';
import {
  createElement,
  CSSProperties,
  FC,
  forwardRef,
  FunctionComponent,
  HTMLProps,
  PropsWithChildren,
  ReactElement,
  ReactHTML
} from 'react';
import { useThemeColorPalette } from '../../hooks/use-theme-color-palette';
import { ThemeColors } from '../../theme';
import { Typofallback } from '../typofallback';
import { TypographyProps } from '../typography';
import classes from './option-item.module.scss';

export type OptionItemProps<P extends object = HTMLProps<HTMLDivElement>>
  = PropsWithChildren<{
    /**
     * Custom classname
     */
    className?: string;

    /**
     * Prefix element
     */
    prefix?: ReactElement;

    /**
     * Suffix element
     */
    suffix?: ReactElement;

    /**
     * Render as active
     */
    selected?: boolean;

    /**
     * Render as focused
     */
    isFocused?: boolean;

    /**
     * Disabled state
     */
    disabled?: boolean;

    /**
     * Custom color definition
     */
    color?: ThemeColors;

    /**
     * Customize render element
     */
    as?: FunctionComponent<P> | keyof ReactHTML;

    /**
     * A boolean variable that specifies whether the suffix size should remain constant.
     * When set to true, the suffix size is fixed and does not change dynamically.
     * When set to false or undefined, the suffix size may vary based on other factors.
     */
    fixedSuffixSize?: boolean;

    /**
     * The `typoProps` variable is an optional property that represents typography-related styling attributes.
     * It specifies a set of properties that can be used to define the appearance and behavior of typography,
     * such as font size, weight, style, alignment, and other text-related settings.
     *
     * This property is primarily used to control the visual design of text and its layout,
     * ensuring consistency in the presentation of textual content.
     */
    typoProps?: TypographyProps;
  }>;

export const OptionItem: FC<OptionItemProps> = forwardRef<
  HTMLDivElement,
  OptionItemProps
>(({
  className,
  children,
  prefix,
  suffix,
  selected,
  isFocused,
  disabled,
  color = 'primary',
  as = 'div',
  fixedSuffixSize,
  typoProps,
  ...props
}, ref) => {
  const palette = useThemeColorPalette(color ?? null);

  return createElement(
    as as keyof ReactHTML,
    {
      ...props,
      ref,
      disabled,
      className: clsx(classes.root, className, 'option-item-component', {
        [classes.selected]: selected,
        [classes.focused]: isFocused,
        [classes.disabled]: disabled,
        [classes.isSuffixFixedSize]: fixedSuffixSize,
      }),
      style: {
        '--option-item--text-color': color === 'primary' ? undefined : palette?.main,
        '--option-item--hover-text-color': color === 'primary' ? palette?.main : palette?.dark,
        '--option-item--hover-background': palette?.['ultra-light'],
      } as CSSProperties,
    },
    <div className={classes.inner}>
      {prefix && <div className={classes.prefix}>{prefix}</div>}
      <Typofallback {...typoProps} className={clsx(typoProps?.className, classes.content)}>{children}</Typofallback>
      {suffix && <div className={classes.suffix}>{suffix}</div>}
    </div>
  );
});
